import { useState, useEffect, useRef, RefObject } from 'react';

interface AutoScrollOptions {
  maxHeight?: number;
  bottomBuffer?: number;
  animationDuration?: number;
  shouldAutoScroll?: boolean;
}

interface AutoScrollResult {
  ref: RefObject<HTMLDivElement>;
  height: string;
  handleScroll: () => void;
  isAutoScrolling: boolean;
  isAutoScrollEnabled: boolean;
  setAutoScrollEnabled: (enabled: boolean) => void;
}

/**
 * A hook to manage auto-scrolling behavior for a container that grows in content
 *
 * @param content The content that causes the container to grow (used as a dependency for updates)
 * @param options Configuration options for the auto-scroll behavior
 * @returns An object containing ref, height, handleScroll function, and auto-scroll state
 */
export function useAutoScroll(
  content: string,
  options?: AutoScrollOptions,
): AutoScrollResult {
  const MAX_HEIGHT = options?.maxHeight ?? 8 * 44; // Default 8 lines of text
  const BOTTOM_BUFFER = options?.bottomBuffer ?? 30; // Default buffer for "at bottom" detection
  const ANIMATION_DURATION = options?.animationDuration ?? 550; // Default animation duration in ms

  // Element reference and state
  const elementRef = useRef<HTMLDivElement>(null);
  const [elementHeight, setElementHeight] = useState('0');
  const [autoScroll, setAutoScroll] = useState(
    options?.shouldAutoScroll ?? true,
  );
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);

  // Track previous height to check for growth
  const oldHeight = useRef(0);

  // Adjust height and scroll when content changes
  useEffect(() => {
    function adjustElementHeight() {
      const element = elementRef.current;
      if (element && autoScroll) {
        const scrollHeight = element.scrollHeight;

        // Only update height if content is growing
        if (scrollHeight > oldHeight.current) {
          setElementHeight(`${scrollHeight}px`);
          oldHeight.current = scrollHeight;

          // Auto-scroll to bottom if overflow
          if (scrollHeight > MAX_HEIGHT) {
            setIsAutoScrolling(true);
            element.scrollTop = scrollHeight;

            // Reset auto-scrolling flag after animation completes
            setTimeout(() => {
              setIsAutoScrolling(false);
            }, ANIMATION_DURATION);
          }
        }
      }
    }

    adjustElementHeight();
  }, [content, autoScroll, MAX_HEIGHT, ANIMATION_DURATION]);

  // Handle scroll events to enable/disable auto-scroll based on user behavior
  const handleScroll = () => {
    // Ignore scroll events triggered by the animation itself
    if (isAutoScrolling) return;

    const element = elementRef.current;
    if (element) {
      // Check if user has scrolled close enough to bottom
      const isAtBottom =
        element.scrollHeight - element.scrollTop <=
        element.clientHeight + BOTTOM_BUFFER;

      // Only enable auto-scroll when user scrolls to bottom
      // Disable it when user scrolls up
      setAutoScroll(isAtBottom);
    }
  };

  return {
    ref: elementRef,
    height: elementHeight,
    handleScroll,
    isAutoScrolling,
    isAutoScrollEnabled: autoScroll,
    setAutoScrollEnabled: setAutoScroll,
  };
}
